import { cn } from '@/lib';
import { LifeInsuranceQuoteCard } from '@client/components/LifeInsuranceQuoteCard';
import type { PaymentOption } from '@client/components/ShopInsuranceModal';
import { TraditionalLTCQuoteCard } from '@client/components/TraditionalLTCQuoteCard';
import { NoQuotesAvailable, QuoteErrorDisplay } from '@client/components/shop-insurance/messages';
import type { TraditionalLTCQuoteWithROI } from '@client/hooks/useTraditionalLTCQuotesWithROI';
import type { Quote } from '@client/model';

interface QuoteWithROI extends Quote {
  roi?: number;
  roiValues?: any;
  formattedROI?: string;
}

interface ShopInsuranceQuotesListProps {
  filteredAdjustedQuotes: QuoteWithROI[];
  filteredTraditionalLTCQuotesWithEligibility: TraditionalLTCQuoteWithROI[];
  underwritingResults: Map<string, 'eligible' | 'ineligible' | 'unsure' | null>;
  expandedIndices: number[];
  selectedTab: ShopInsuranceTab;
  clientPartner: any;
  premiumValue: number;
  paymentOption: PaymentOption;
  advisorId: string;
  isQuoteSelected: (quote: any) => { isSelected: boolean; fundingSourceId: string | null };
  handleCardToggle: (index: number) => void;
  handleViewUnderwritingDetails: (quoteId: string, productName: string) => void;
  onClose: () => void;
  knownLimitationError?: QuoteError | null;
}

export function ShopInsuranceQuotesList({
  filteredAdjustedQuotes,
  filteredTraditionalLTCQuotesWithEligibility,
  underwritingResults,
  expandedIndices,
  selectedTab,
  clientPartner,
  premiumValue,
  paymentOption,
  advisorId,
  isQuoteSelected,
  handleCardToggle,
  handleViewUnderwritingDetails,
  onClose,
  knownLimitationError,
}: ShopInsuranceQuotesListProps) {
  if (knownLimitationError) {
    return <QuoteErrorDisplay error={knownLimitationError} />;
  }

  const hasFilteredQuotes =
    filteredAdjustedQuotes.length > 0 || filteredTraditionalLTCQuotesWithEligibility.length > 0;

  if (!hasFilteredQuotes) {
    return <NoQuotesAvailable advisorId={advisorId} />;
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-4 pb-24 md:pb-28 lg:h-[calc(100vh-320px)] lg:overflow-y-auto lg:pb-36',
      )}
      data-testid="shop-insurance-quotes-list"
    >
      {filteredAdjustedQuotes.map((quote: QuoteWithROI, index: number) => {
        const { isSelected, fundingSourceId } = isQuoteSelected(quote);
        const underwritingResult = underwritingResults.get(quote.insuranceQuoteId);

        return (
          <LifeInsuranceQuoteCard
            key={`${quote.insuranceQuoteId}-${premiumValue}`}
            testId="shop-insurance-quote-card"
            quote={quote}
            isExpanded={expandedIndices.includes(index)}
            onToggleExpand={() => handleCardToggle(index)}
            onClose={onClose}
            hasHighestROI={index === 0}
            isSelected={isSelected}
            fundingSourceId={fundingSourceId}
            selectedTab={selectedTab}
            clientPartner={clientPartner}
            selectedPremium={premiumValue}
            underwritingResult={underwritingResult}
            onViewUnderwritingDetails={() =>
              handleViewUnderwritingDetails(quote.insuranceQuoteId, quote.productName)
            }
          />
        );
      })}

      {filteredTraditionalLTCQuotesWithEligibility.map((quote, index) => {
        const adjustedIndex = filteredAdjustedQuotes.length + index;
        const { isSelected, fundingSourceId } = isQuoteSelected(quote);
        const underwritingResult = underwritingResults.get(quote.traditionalLTCQuoteId);

        return (
          <TraditionalLTCQuoteCard
            key={`${quote.traditionalLTCQuoteId}-${paymentOption}`}
            testId="shop-insurance-quote-card"
            quote={quote}
            isExpanded={expandedIndices.includes(adjustedIndex)}
            onToggleExpand={() => handleCardToggle(adjustedIndex)}
            onClose={onClose}
            isSelected={isSelected}
            fundingSourceId={fundingSourceId}
            underwritingResult={underwritingResult}
            onViewUnderwritingDetails={() =>
              handleViewUnderwritingDetails(quote.traditionalLTCQuoteId, quote.productName)
            }
          />
        );
      })}
    </div>
  );
}
